/**
 * PlatformAlertsPanel -- Founder "Override Alerting" safety net (2026-09-07).
 *
 * ForgedOps Platform Admin / Operations layer ONLY -- never rendered in
 * the customer-facing Company Admin. Surfaces unacked billing/entitlement
 * override alerts (created, expiring soon, expired, Stripe action
 * failure, reconciliation failure, divergence, unusually long) with a
 * one-click Acknowledge action. Email delivery to platform admins
 * happens server-side (services/platform_alerts.py) regardless of
 * whether this panel is open.
 *
 * Endpoints: GET /api/platform/alerts, POST /api/platform/alerts/{id}/ack
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BellRing, CheckCircle2, Loader2, AlertOctagon, Info } from "lucide-react";

const SEVERITY_META = {
  critical: { color: "#fca5a5", Icon: AlertOctagon },
  warning: { color: "#fbbf24", Icon: BellRing },
  info: { color: "#7dd3fc", Icon: Info },
};

function fmt(dt) {
  if (!dt) return null;
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

export default function PlatformAlertsPanel() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/alerts", { params: { unacked_only: !showAll } });
      setAlerts(data.alerts || []);
      setError(null);
    } catch (err) {
      // Founder review (iteration_219): a silent swallow here was
      // indistinguishable from "no alerts" for an admin whose session
      // lacks MFA/permission -- surface it instead of hiding it.
      setAlerts([]);
      setError(err?.response?.status === 403 ? "MFA required to view commercial alerts." : "Alerts unavailable right now.");
    } finally {
      setLoading(false);
    }
  }, [showAll]);

  useEffect(() => { load(); }, [load]);

  async function ack(id) {
    setBusyId(id);
    try {
      await api.post(`/platform/alerts/${id}/ack`);
      await load();
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div data-testid="platform-alerts-panel" className="text-xs text-[var(--fop-text-3)] px-1">Loading alerts…</div>;
  if (!showAll && alerts.length === 0 && !error) return null;

  return (
    <div className="rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden"
         data-testid="platform-alerts-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--fop-border)]">
        <div className="flex items-center gap-2 text-sm font-medium text-[var(--fop-text)]">
          <BellRing size={15} className="text-amber-400" />
          Commercial Alerts
          {alerts.length > 0 && !showAll && (
            <span className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300" data-testid="platform-alerts-unacked-count">
              {alerts.length} unacknowledged
            </span>
          )}
        </div>
        <button
          className="text-[11px] uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
          onClick={() => setShowAll((v) => !v)}
          data-testid="platform-alerts-toggle-all"
        >
          {showAll ? "Show unacknowledged only" : "Show all"}
        </button>
      </div>

      {error ? (
        <div className="px-4 py-4 text-xs text-amber-300" data-testid="platform-alerts-error">{error}</div>
      ) : alerts.length === 0 ? (
        <div className="px-4 py-4 text-xs text-[var(--fop-text-3)]">No alerts.</div>
      ) : (
        <div className="divide-y divide-[var(--fop-border)]">
          {alerts.map((a) => {
            const meta = SEVERITY_META[a.severity] || SEVERITY_META.info;
            const Icon = meta.Icon;
            return (
              <div key={a.id} className="flex items-start gap-3 px-4 py-3" data-testid={`platform-alert-row-${a.id}`}>
                <Icon size={14} style={{ color: meta.color }} className="mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[var(--fop-text)]">{a.title}</div>
                  <div className="text-[11px] text-[var(--fop-text-3)] mt-0.5">{a.company_name} · {fmt(a.created_at)}</div>
                  <div className="text-xs text-[var(--fop-text-2)] mt-1">{a.body}</div>
                </div>
                {!a.acked && (
                  <Button size="sm" variant="outline" onClick={() => ack(a.id)} disabled={busyId === a.id}
                          data-testid={`platform-alert-ack-button-${a.id}`}>
                    {busyId === a.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} className="mr-1" />}
                    Acknowledge
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
